import { ApiAnnotation, ApiSpine } from '@mhe/reader/models';
import { createReducer, on } from '@ngrx/store';

import {
  addAnnotation,
  addAnnotations,
  addAnnotationsBySpineId,
  clearAllAnnotations,
  exportSuccess,
  fetchDistinctSpineIdsSuccess,
  removeAnnotation,
  removeBatchAnnotations,
  removeHighlight,
  removeNote,
  selectAllAnnotationsInList,
  clickedAnnotation,
  setSpineItemsToSpineId,
  startExport,
  updateSpinesStatus,
  selectAnnotationBySpineId,
  selectAnnotationByResourceUUID,
  deselectAllAnnotationsInList,
  setOrphanAnnotations,
} from './annotations.actions';
import { AnnotationsState, initialState } from './annotations.state';

function addAnnotationsToStateBySpineId(state: AnnotationsState, spineId: string, annotations: ApiAnnotation[]): any {
  return {
    ...state.entitiesBySpineId,
    [spineId]: annotations.reduce(
      (previous, annotation): Record<string, ApiAnnotation> => ({
        ...previous,
        [annotation.resourceUUID]: {
          ...annotation,
          selected: false,
        },
      }),
      { ...state.entitiesBySpineId[spineId] },
    ),
  };
}

function findSpineId(
  spines: Record<string, Record<string, ApiAnnotation>>,
  annotationId: string,
): string | undefined {
  for (const spineId in spines) {
    if (spines[spineId][annotationId]) {
      return spineId;
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function annotationType(
  annotation: ApiAnnotation,
): 'placemark' | 'note' | 'highlight' | undefined {
  // if has placemarkText, it's a placemark
  // if has noteText, it's a note
  // if highlight = True
  if (annotation.placemarkText) {
    return 'placemark';
  }
  if (annotation.note) {
    return 'note';
  }
  if (annotation.highlight) {
    return 'highlight';
  }
}

function removeSingleAnnotation(
  state: AnnotationsState,
  spineID: string | undefined,
  resourceUUID: string,
): AnnotationsState {
  const newState = removeAnnotationById(state, spineID, resourceUUID);
  return {
    ...newState,
    spines: fixSpines(
      newState.entitiesBySpineId[spineID as string],
      spineID,
      newState,
    ),
  };
}

function fixSpines(
  annotations: Record<string, ApiAnnotation>,
  spineID: string | undefined,
  state: AnnotationsState,
): ApiSpine[] {
  // go to the spine
  // count annotations group by type
  // if 0, update the flag in state.spines
  let placemarks = 0;
  let notes = 0;
  let highlights = 0;
  for (const annotationId of Object.keys(annotations)) {
    const annotation = annotations[annotationId];
    if (annotation.placemarkText) {
      placemarks++;
    }
    if (annotation.note) {
      notes++;
    }
    if (annotation.highlight) {
      highlights++;
    }
  }
  const oldSpine: ApiSpine = state.spines.find(
    (s) => s.spineID === spineID,
  ) as ApiSpine;
  const newSpine = {
    spineID: oldSpine?.spineID || spineID,
    hasHighlight: highlights > 0,
    hasNote: notes > 0,
    hasPlacemark: placemarks > 0,
    // only add spineDataRequested if present on existing spine
    ...(oldSpine?.spineDataRequested && {
      spineDataRequested: oldSpine.spineDataRequested,
    }),
  };
  return state.spines
    .filter((s) => s.spineID !== spineID)
    .concat(newSpine as ApiSpine);
}

function removeAnnotationById(
  state: AnnotationsState,
  spineID,
  resourceUUID,
): AnnotationsState {
  const spines: Record<string, Record<string, ApiAnnotation>> = state.entitiesBySpineId;
  const annotations = { ...spines[spineID] };
  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
  delete annotations[resourceUUID];

  const fixedSpines = fixSpines(annotations, spineID, state);
  const newState = {
    ...state,
    entitiesBySpineId: {
      ...spines,
      [spineID]: annotations,
    },
    spines: fixedSpines,
  };
  return newState;
}

export const annotationsReducer = createReducer(
  initialState,
  on(addAnnotationsBySpineId, (state, { spineId, annotations, loaded }) => ({
    ...state,
    loaded,
    entitiesBySpineId: addAnnotationsToStateBySpineId(
      state,
      spineId,
      annotations,
    ),
  })),
  on(addAnnotations, (state, { annotations, loaded }) => ({
    ...state,
    loaded,
    entitiesBySpineId: annotations.reduce(
      (previous, annotation) => {
        return {
          ...previous,
          [annotation.spineID]: {
            ...previous[annotation.spineID],
            [annotation.resourceUUID]: {
              ...annotation,
              selected: false,
            },
          },
        };
      },
      { ...state.entitiesBySpineId },
    ),
  })),
  on(addAnnotation, (state, { annotation }) => {
    const annotations = {
      ...state.entitiesBySpineId[annotation.spineID],
      [annotation.resourceUUID]: {
        ...annotation,
        selected: false,
      },
    } satisfies Record<string, ApiAnnotation>;
    return {
      ...state,
      entitiesBySpineId: {
        ...state.entitiesBySpineId,
        [annotation.spineID]: annotations,
      },
      spines: fixSpines(
        annotations,
        annotation.spineID,
        state,
      ),
    };
  }),
  on(
    removeAnnotation,
    (state: AnnotationsState, { annotation: { spineID, resourceUUID } }) => {
      return removeSingleAnnotation(state, spineID, resourceUUID);
    },
  ),
  on(
    removeBatchAnnotations,
    (state: AnnotationsState, { annotations }) => {
      let newState = state;
      annotations.forEach(annotation => {
        newState = removeSingleAnnotation(newState, annotation.spineID, annotation.resourceUUID);
      });
      return newState;
    },
  ),
  // TODO: this will probably go to an effect when we are storing to the api.
  on(removeHighlight, removeNote, (state: AnnotationsState, { id }) => {
    const spineId = findSpineId(state.entitiesBySpineId, id);
    const newState = spineId ? removeAnnotationById(state, spineId, id) : state;
    return {
      ...newState,
      spines: fixSpines(
        newState.entitiesBySpineId[spineId as string],
        spineId,
        newState,
      ),
    };
  }),
  on(clearAllAnnotations, (state) => ({
    ...state,
    loaded: false,
    progress: 0,
    spines: [],
    entitiesBySpineId: {},
  })),
  on(fetchDistinctSpineIdsSuccess, (state, { spines }) => ({
    ...state,
    spines,
    loaded: true,
  })),
  on(updateSpinesStatus, (state, { spineId }) => ({
    ...state,
    spines: state.spines.map((spine: ApiSpine) => {
      const cleanSpine = { ...spine };
      if (cleanSpine.spineID === spineId) {
        cleanSpine.spineDataRequested = true;
      }
      return cleanSpine;
    }),
  })),
  on(startExport, (state) => ({
    ...state,
    isExporting: true,
  })),
  on(exportSuccess, (state) => ({
    ...state,
    isExporting: false,
  })),
  on(setSpineItemsToSpineId, (state, { spineId, spineItems }) => {
    const matchSpineId = state.spines.find(spine => spine.spineID === spineId);
    if (!matchSpineId) {
      console.warn('setSpineItemsToSpineId', 'spine not found');
      return { ...state };
    }
    return {
      ...state,
      spines: state.spines.map(spine => {
        if (spine.spineID === spineId) {
          return {
            ...spine,
            spineItems,
          };
        }
        return spine;
      }),
    };
  }),
  on(clickedAnnotation, (state, { annotation }) => ({
    ...state,
    selectedAnnotation: annotation,
  })),
  // EDIT MODE REDUCERS
  on(
    selectAllAnnotationsInList,
    (state, { selected }) => ({
      ...state,
      entitiesBySpineId: Object.keys(state.entitiesBySpineId).reduce(
        (acc, spineId) => {
          acc[spineId] = {
            ...state.entitiesBySpineId[spineId],
            ...Object.keys(state.entitiesBySpineId[spineId]).reduce(
              (acc2, annotationId) => {
                const annotation = state.entitiesBySpineId[spineId][annotationId];
                acc2[annotationId] = {
                  ...annotation,
                  selected,
                };
                return acc2;
              },
              {},
            ),
          };
          return acc;
        },
        {},
      ),
    }),
  ),
  on(
    selectAnnotationBySpineId,
    (state, { spineId, selected }) => ({
      ...state,
      entitiesBySpineId: {
        ...state.entitiesBySpineId,
        [spineId]: {
          ...state.entitiesBySpineId[spineId],
          ...Object.keys(state.entitiesBySpineId[spineId]).reduce(
            (acc2, annotationId) => {
              const annotation = state.entitiesBySpineId[spineId][annotationId];
              acc2[annotationId] = {
                ...annotation,
                selected,
              };
              return acc2;
            },
            {},
          ),
        },
      },
    }),
  ),
  on(
    selectAnnotationByResourceUUID,
    (state, { spineId, resourceUuid, selected }) => ({
      ...state,
      entitiesBySpineId: {
        ...state.entitiesBySpineId,
        [spineId]: {
          ...state.entitiesBySpineId[spineId],
          [resourceUuid]: {
            ...state.entitiesBySpineId[spineId][resourceUuid],
            selected,
          },
        },
      },
    }),
  ),
  on(
    deselectAllAnnotationsInList,
    (state) => ({
      ...state,
      entitiesBySpineId: Object.keys(state.entitiesBySpineId).reduce(
        (acc, spineId) => {
          acc[spineId] = {
            ...state.entitiesBySpineId[spineId],
            ...Object.keys(state.entitiesBySpineId[spineId]).reduce(
              (acc2, annotationId) => {
                const annotation = state.entitiesBySpineId[spineId][annotationId];
                acc2[annotationId] = {
                  ...annotation,
                  selected: false,
                };
                return acc2;
              },
              {},
            ),
          };
          return acc;
        },
        {},
      ),
    }),
  ),
  // END EDIT MODE REDUCERS
  on(
    setOrphanAnnotations,
    (state, { orphans }) => ({
      ...state,
      entitiesBySpineId: orphans.reduce(
        (acc, orphan) => {
          const { resourceUUID, spineID } = orphan;
          let existingAnnotations = acc[spineID];
          existingAnnotations = {
            ...existingAnnotations,
            [resourceUUID]: {
              ...existingAnnotations[resourceUUID],
              orphan: true,
            },
          };
          return {
            ...acc,
            [spineID]: existingAnnotations,
          };
        },
        state.entitiesBySpineId,
      ),
    }),
  ),
);
